import { create } from 'zustand'

import { DEFAULT_FEDERAL_GRANT_FILTER } from '@/constants/search/federal/grant'

import { setRequest } from '@/stores/common/search'

import type { UUID } from '@/types/Id'
import type {
  SearchDataResponse,
  SearchRequest,
  SearchRequestFilter,
  SearchStore,
} from '@/types/Search'

export const useFederalGrantSearchStore = create<SearchStore>()((set, get) => {
  return {
    augmentedQueries: [],
    autoSubmit: true,
    createdPursuits: new Map(),
    data: undefined,
    dataIsLoading: false,
    filter: {
      type: 'federal_grant',
      filter: DEFAULT_FEDERAL_GRANT_FILTER,
    },
    filterDrawerIsOpen: false,
    isStreaming: false,
    paginationIsLoading: false,
    query: '',
    previousRequest: undefined,
    request: undefined,
    steps: [],
    summary: '',
    summaryIsLoading: false,
    useFullTextSearch: false,
    addCreatedPursuit: (id: string, pursuitId: UUID, pursuitStageId: UUID) => {
      const createdPursuits = get().createdPursuits
      createdPursuits.set(id, { pursuitId, pursuitStageId })
      set((state) => ({ ...state, createdPursuits }))
    },
    removeCreatedPursuit: (id: string) => {
      const createdPursuits = get().createdPursuits
      createdPursuits.delete(id)
      set((state) => ({ ...state, createdPursuits }))
    },
    setAugmentedQueries: (augmentedQueries: Array<string>) => {
      set((state) => ({ ...state, augmentedQueries }))
    },
    setAutoSubmit: (autoSubmit: boolean) => {
      set((state) => ({ ...state, autoSubmit }))
    },
    setData: (data: SearchDataResponse) => {
      set((state) => ({ ...state, data }))
    },
    setDataIsLoading: (dataIsLoading: boolean) => {
      set((state) => ({ ...state, dataIsLoading }))
    },
    setFilter: (filter: SearchRequestFilter | null) => {
      set((state) => ({ ...state, filter }))
    },
    setFilterDrawerIsOpen: (open: boolean) => {
      set((state) => ({ ...state, filterDrawerIsOpen: open }))
    },
    setIsStreaming: (isStreaming: boolean) => {
      set((state) => ({ ...state, isStreaming }))
    },
    setQuery: (query: string) => {
      set((state) => ({ ...state, query }))
    },
    setPreviousRequest: (request: SearchRequest) => {
      set((state) => ({ ...state, previousRequest: request }))
    },
    setRequest: (request: SearchRequest, accessToken?: string | null) => {
      setRequest(request, set, get, accessToken)
      set((state) => ({ ...state, request, previousRequest: request }))
    },
    setSteps: (steps: string[]) => {
      set((state) => ({ ...state, steps }))
    },
    setSummary: (summary: string) => {
      set((state) => ({ ...state, summary }))
    },
    setSummaryIsLoading: (summaryIsLoading: boolean) => {
      set((state) => ({ ...state, summaryIsLoading }))
    },
    setUseFullTextSearch: (useFullTextSearch: boolean) => {
      set((state) => ({ ...state, useFullTextSearch }))
    },
  }
})

const selectSetRequest = (state: SearchStore) => state.setRequest
export const useSetFederalGrantSearchRequest = () => {
  return useFederalGrantSearchStore(selectSetRequest)
}

const selectSetQuery = (state: SearchStore) => state.setQuery
export const useSetFederalGrantSearchQuery = () => {
  return useFederalGrantSearchStore(selectSetQuery)
}

const selectSetFilter = (state: SearchStore) => state.setFilter
export const useSetFederalGrantSearchFilter = () => {
  return useFederalGrantSearchStore(selectSetFilter)
}
